import React, { type ReactNode } from 'react';
import { styled } from '@compiled/react';
import { graphql, useFragment, useMutation } from 'react-relay';
import StandardButton from '@atlaskit/button/standard-button';
import ChevronDownIcon from '@atlaskit/icon/utility/migration/chevron-down';
import ChevronRightIcon from '@atlaskit/icon/utility/migration/chevron-right';
import { token } from '@atlaskit/tokens';
import fireErrorAnalytics from '@atlassian/jira-errors-handling/src/utils/fire-error-analytics.tsx';
import { useIntl } from '@atlassian/jira-intl';
import { fireUIAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import type {
	ui_collapsibleUiTriggerButton_updateCollapsedUisMutation,
	ui_collapsibleUiTriggerButton_updateCollapsedUisMutation$data,
} from '@atlassian/jira-relay/src/__generated__/ui_collapsibleUiTriggerButton_updateCollapsedUisMutation.graphql';
import type {
	ui_softwareReleasesCollapsibleUiToggle_CollapsibleSection$key,
	JiraVersionDetailsCollapsedUi,
} from '@atlassian/jira-relay/src/__generated__/ui_softwareReleasesCollapsibleUiToggle_CollapsibleSection.graphql';
import messages from './messages.tsx';
import { checkIsCollapsed } from './utils.tsx';

const getNewCollapsedUis = (
	currentState: JiraVersionDetailsCollapsedUi[],
	type: JiraVersionDetailsCollapsedUi,
	isCurrentlyCollapsed: boolean,
): JiraVersionDetailsCollapsedUi[] =>
	isCurrentlyCollapsed ? currentState.filter((value) => value !== type) : [...currentState, type];

type Props = {
	jiraVersion: ui_softwareReleasesCollapsibleUiToggle_CollapsibleSection$key;
	uiType: JiraVersionDetailsCollapsedUi;
	renderHeader: (isCollapsed: boolean, onToggleClicked: () => void) => ReactNode;
	renderBody: () => ReactNode;
};

interface CollapsibleToggleButtonProps {
	isCollapsed: boolean;
	onClick?: () => void;
}

export const CollapsibleToggleButton = ({ isCollapsed, onClick }: CollapsibleToggleButtonProps) => {
	const { formatMessage } = useIntl();
	const icon = isCollapsed ? (
		<ChevronRightIcon spacing="spacious" label={formatMessage(messages.collapsedIconLabel)} />
	) : (
		<ChevronDownIcon spacing="spacious" label={formatMessage(messages.expandedIconLabel)} />
	);

	return <StandardButton appearance="subtle" iconBefore={icon} onClick={onClick} />;
};

export const CollapsibleSection = ({ jiraVersion, uiType, renderHeader, renderBody }: Props) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const version = useFragment(
		graphql`
			fragment ui_softwareReleasesCollapsibleUiToggle_CollapsibleSection on JiraVersion {
				collapsedUis @optIn(to: ["JiraVersionCollapsedUis"])
				id @required(action: THROW)
			}
		`,
		jiraVersion,
	);

	const collapsedUis = (version.collapsedUis ?? []).filter(Boolean);

	const [updateCollapsedUis] =
		useMutation<ui_collapsibleUiTriggerButton_updateCollapsedUisMutation>(graphql`
			mutation ui_collapsibleUiTriggerButton_updateCollapsedUisMutation(
				$input: JiraVersionDetailsCollapsedUisInput!
			) @raw_response_type {
				jira {
					saveVersionDetailsCollapsedUis(input: $input)
						@optIn(to: ["JiraSaveVersionDetailsCollapsedUis", "JiraVersionCollapsedUis"]) {
						success
						version {
							collapsedUis
						}
					}
				}
			}
		`);
	const onError = () => {
		fireErrorAnalytics({
			meta: {
				id: 'jira.releases.version.detail.collapsed-uis.user-preference.mutation.error.id',
				packageName: 'jiraSoftwareReleasesCollapsibleUiToggle',
				teamName: 'nova',
			},
			error: Error('Unable to update collapsed ui state'),
			sendToPrivacyUnsafeSplunk: true,
		});
	};
	const saveUserPreference = (newCollapsedUis: JiraVersionDetailsCollapsedUi[]) => {
		updateCollapsedUis({
			variables: {
				input: {
					collapsedUis: newCollapsedUis,
					versionId: version.id,
				},
			},
			onCompleted: (data: ui_collapsibleUiTriggerButton_updateCollapsedUisMutation$data) => {
				if (!data.jira?.saveVersionDetailsCollapsedUis?.success) {
					onError();
				}
			},
			onError,
			optimisticResponse: {
				jira: {
					saveVersionDetailsCollapsedUis: {
						success: true,
						version: {
							collapsedUis: newCollapsedUis,
							id: version.id,
						},
					},
				},
			},
		});
	};

	const isCollapsed = collapsedUis.includes(uiType);

	return (
		<>
			{renderHeader(isCollapsed, () => {
				fireUIAnalytics(
					createAnalyticsEvent({
						action: 'toggled',
						actionSubject: 'collapsedUiToggleButon',
					}),
					'versionDetailsCollapsedUis',
					{ uiType, action: isCollapsed ? 'expand' : 'collapse' },
				);
				saveUserPreference(getNewCollapsedUis(collapsedUis, uiType, isCollapsed));
			})}
			<CollapsibleContent isCollapsed={checkIsCollapsed(version.collapsedUis, uiType)}>
				{renderBody()}
			</CollapsibleContent>
		</>
	);
};

interface CollapsibleContentProps {
	isCollapsed: boolean;
}
// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const CollapsibleContent = styled.div<CollapsibleContentProps>({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	display: `${({ isCollapsed }: CollapsibleContentProps) => (isCollapsed ? 'none' : 'block')}`,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	marginBottom: `${({ isCollapsed }: CollapsibleContentProps) =>
		isCollapsed ? token('space.500') : token('space.0')}`,
});
